// Room names and the game-hut signs: where they go, how big, in what colours (deck/entities.js draws them with
// Phaser; window.__deck.labels shows the last frame's). Screen pixels throughout.
// - A room's name sits on a small cream plaque with pixel corners, centred where the bare name used to be (on the
//   room's top wall; doors are at the bottom). Its text is 14px at the everyday zoom (2x and up), 18px zoomed out.
// - A game hut's sign keeps a readable size: its font is never under 16px, and the box and lights scale with the
//   font, not with the map (at 3x it is the old 15px sign, a pixel bigger).
import { measure } from './text.js';
import { t } from '/i18n.js';

export const INK = 0x1b1410, YELLOW = 0xfdd03b, CREAM = 0xfff8e7;
export const SIGN_DIM = 0xfacc15, SIGN_TEXT_ON = 0x7c3aed, LIGHT_A = 0xf472b6, LIGHT_B = 0x22d3ee;
/** 0x1b1410 -> '#1b1410', for the canvas and for Phaser Text colours */
export const css = (c) => '#' + c.toString(16).padStart(6, '0');

/** A hut sign: text, pixel icons ({icon}: a frame of /icons/ui-icons.png, deck/entities.js draws it) and catalog texts
 *  ({text: key}: worded in the reader's language each frame, segText). */
export const SIGNS = {
  game: [{ icon: 'gamepad' }, { text: 'deck.sign.pictionary' }],
  spy: [{ icon: 'spy' }, { text: 'deck.sign.spy' }, ' · ', { icon: 'wolf' }, { text: 'deck.sign.werewolf' }], // the spy hut plays both, one at a time
  bonk: [{ icon: 'sword' }, 'OtterBonk'],
  mind: [{ icon: 'heart' }, { text: 'mind.sign' }], // 心灵捕手 (games-mind.js)
  // the 码头 / 跳板 between the boats (map portals, drawn with the huts' signs: deck/scene.js)
  'dock-play': [{ icon: 'party' }, { text: 'deck.sign.dockPlay' }],
  'dock-main': [{ icon: 'desk' }, { text: 'deck.sign.dockMain' }],
};
/** A sign part's words (null for an icon). The games boat's name is one catalog key (floors.name.play). */
export const segText = (s) => (typeof s === 'string' ? s : s.text ? t(s.text, { play: t('floors.name.play') }) : null);
/** A sign's words alone (what it says, for debugging and tests) */
export const signText = (id) => SIGNS[id].map(segText).filter((s) => s !== null).join('');
/** The size an icon is drawn at next to text of px pixels: whole multiples of 16 (crisp), never under 16. */
export const iconPx = (px) => 16 * Math.max(1, Math.floor(px / 16));
export const isSign = (z) => z.id in SIGNS;
/** The 码头 / 跳板 (map portals) as label zones: their sign stands on the portal's near end (a hut's sits above its roof,
 *  2.6 tiles over the zone's top), so whoever lands by it isn't standing under it. */
export const portalLabels = (portals = []) => portals.map((q) => ({ ...q, y: q.y + 2.6 }));
/** A room name's size: 14px at 2x and up, 18px below. */
export const roomPx = (scale) => (scale >= 2 ? 14 : 18);
export const roomFont = (scale) => `bold ${roomPx(scale)}px system-ui`;
/** A sign's size: as before (5 px per zoom step) but never under 16px. */
export const signPx = (scale) => Math.max(16, Math.round(5 * scale));
export const signFont = (scale) => `bold ${signPx(scale)}px system-ui`;

/** Keep a box of width w on screen if its room's centre is (labels of rooms off screen stay off screen). */
const keepOn = (x, w, cx, width) => (cx < 0 || cx > width ? x : Math.max(4, Math.min(width - 4 - w, x)));

/** A room's plaque: {font, px, text, x (text left), baseline, box: {x, y, w, h}, rects: [[x, y, w, h, colour]...]}. */
export function roomPlaque(z, cam) {
  const S = cam.S(), px = roomPx(cam.scale), font = roomFont(cam.scale), tw = measure(font, z.name);
  const cx = cam.x + (z.x + z.w / 2) * S, nameY = cam.y + (z.y + 0.2) * S; // where the bare name's baseline was
  const w = Math.round(tw + 14), h = px + 8;
  const x = Math.round(keepOn(cx - w / 2, w, cx, cam.width)), y = Math.round(nameY - 5 - h / 2);
  return {
    font, px, text: z.name, x: Math.round(x + (w - tw) / 2), baseline: y + Math.round(h / 2 + px * 0.36), box: { x, y, w, h },
    // a 2 px ink edge with its corners stepped in (pixel corners), cream inside
    rects: [[x + 2, y, w - 4, h, INK], [x, y + 2, w, h - 4, INK], [x + 3, y + 2, w - 6, h - 4, CREAM], [x + 2, y + 3, w - 4, h - 6, CREAM]],
  };
}

/** A game hut's sign at time `now`: {font, px, text, on, x (text left), baseline, colour, box, fill: [x, y, w, h, colour],
 *  lights: [[x, y, w, h, colour]...], parts: [{x, text} | {x, y, icon, size}] (left to right, screen pixels)}.
 *  Drawn as: fill, a 2 px ink strokeRect on the box, the parts (text and icons), lights. */
export function hutSign(z, cam, now) {
  const S = cam.S(), px = signPx(cam.scale), font = signFont(cam.scale), u = px / 5; // u: the old SCALE, from the font
  const text = signText(z.id), on = Math.floor(now / 500) % 2 === 0, ip = iconPx(px), gap = Math.round(px / 4);
  const segs = SIGNS[z.id].map((s) => { const w = segText(s); return w !== null ? { text: w, w: measure(font, w) } : { icon: s.icon, w: ip + gap }; });
  const tw = segs.reduce((a, s) => a + s.w, 0);
  const cx = cam.x + (z.x + z.w / 2) * S, sy = cam.y + (z.y - 2.6) * S, w = tw + 6 * u;
  const R = Math.round, x = keepOn(cx - w / 2, w, cx, cam.width), box = { x: R(x), y: R(sy - 6 * u), w: R(w), h: R(9 * u) }; // whole pixels: crisp edges
  const lights = [];
  for (let i = 0; i < 6; i++) lights.push([R(x + (i + 0.5) * (w / 6) - u), R(sy - 8 * u), R(2 * u), R(2 * u), (i + Math.floor(now / 250)) % 2 ? LIGHT_A : LIGHT_B]);
  const baseline = R(sy + 1.5 * u), mid = baseline - px * 0.36; // the text's middle: icons are centred on it
  let px0 = x + 3 * u;
  const parts = segs.map((s) => { const p = s.icon ? { x: R(px0), y: R(mid - ip / 2), icon: s.icon, size: ip } : { x: R(px0), text: s.text }; px0 += s.w; return p; });
  return { font, px, text, on, x: R(x + 3 * u), baseline, colour: on ? SIGN_TEXT_ON : INK, box, fill: [box.x, box.y, box.w, box.h, on ? YELLOW : SIGN_DIM], lights, parts };
}

/** The space a layout takes on screen (a sign with its lights over it, or a plaque), px: {x0, y0, x1, y1}. */
const extent = (L) => { const top = Math.min(L.box.y, ...(L.lights ?? []).map((r) => r[1])); return { x0: L.box.x, y0: top, x1: L.box.x + L.box.w, y1: L.box.y + L.box.h }; };
/** Move a hut sign's layout dy px up or down (its box, fill, lights, icons and text together). */
function shiftSign(L, dy) {
  L.box.y += dy; L.fill[1] += dy; L.baseline += dy;
  for (const r of L.lights) r[1] += dy;
  for (const p of L.parts) if (p.icon) p.y += dy;
}
/** Hut signs never overlap one another or a room's plaque, at any zoom: huts side by side (the 活动舱's row of four) have
 *  signs wider than their huts once zoomed out, so a sign that would run into one already placed goes up over it, as
 *  many times as it takes (a stack, left to right). Plaques stay where they are. Changes the sign layouts in place. */
export function unstackSigns(signs, plaques = [], gap = 4) {
  const placed = plaques.map(extent);
  for (const L of [...signs].sort((a, b) => a.box.x - b.box.x)) {
    for (let tries = 0; tries < 8; tries++) {
      const e = extent(L), hit = placed.find((o) => e.x0 < o.x1 + gap && o.x0 < e.x1 + gap && e.y0 < o.y1 + gap && o.y0 < e.y1 + gap);
      if (!hit) break;
      shiftSign(L, hit.y0 - gap - e.y1); // its bottom just over the one it ran into
    }
    placed.push(extent(L));
  }
  return signs;
}
